/**
 * Run view — serves both /runs/:id (a run you may have just launched or are
 * reattaching to) and /conversations/:id (a finished run, read-only).
 *
 * Cold-load lifecycle:
 *   1. GET /api/runs/:id for the persisted record.
 *   2. status === 'running'              → poll every ~2s until terminal/awaiting;
 *        show the theater + reduced journey (never the technical trace).
 *      status === 'awaiting_user_response' → seed a PlaygroundSession into
 *        `awaiting` from `pendingSuspension` so the question card + reply form
 *        can drive a resume on this fresh page; render LiveRunView.
 *      terminal                          → deliverable first (result card with
 *        summary strip), then the theater tableau + journey recap.
 *   3. "Your request" (the persisted input) closes the page as reference.
 *
 * Cancel hits POST /api/runs/:id/cancel (not just session.cancel()).
 */
import {
  adaptHeartbeat,
  groupFanouts,
  Icon,
  Notice,
  type PersistedTraceEvent,
  type PlaygroundResumeReply,
  Spinner,
  type StreamingAwaiting,
  type TraceEvent
} from '@evt/flow-stream-ui'
import type { PendingSuspension, RunDetail, RunResultView } from '@portal/contracts'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { listFlows } from '../api/flows'
import { PortalApiError } from '../api/portal-client'
import { cancelRun, getRun, getRunResult, retryResult, retryResultDelay, runFileUrl } from '../api/runs'
import { currentActivity, questionsAsked, runStats } from '../components/journey'
import { LiveRunView } from '../components/LiveRunView'
import { RunJourney } from '../components/RunJourney'
import { RunRequest } from '../components/RunRequest'
import { type ResultMeta, RunResult } from '../components/RunResult'
import { keys } from '../hooks/queryClient'
import { useReattachSession } from '../hooks/use-reattach-session'
import { describeApiError, useI18n } from '../i18n'
import { RunTheater } from '../theater/RunTheater'

const POLL_MS = 2_000

export function RunView({ readOnly = false }: { readOnly?: boolean }) {
  const { t, formatDateTime } = useI18n()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const decodedId = id ? decodeURIComponent(id) : ''

  const [pollMs, setPollMs] = useState<number | false>(false)
  const runQuery = useQuery({
    queryKey: keys.conversation(decodedId),
    queryFn: () => getRun(decodedId),
    enabled: !!decodedId,
    refetchInterval: pollMs
  })
  const flows = useQuery({ queryKey: keys.flows(), queryFn: listFlows })

  const detail = runQuery.data
  // A run whose last reply failed while its pause still holds takes a reply like an awaiting one.
  const answerable = detail?.resumable !== undefined
  const isAwaiting = detail?.status === 'awaiting_user_response' || answerable
  const isRunning = detail?.status === 'running'
  const isTerminal =
    !answerable &&
    (detail?.status === 'success' || detail?.status === 'partial_success' || detail?.status === 'failure')

  // The persisted deliverable (captured by the BFF as the run streamed). The BFF says which 404 it is:
  // `result_pending` while it is still pulling the run's files into its store (the page asks again every 3 s),
  // `result_not_found` when there is nothing to wait for (a mail-reader run, a lost stream, a failed capture, an
  // expired result) — degrade at once to the status/issues card without files.
  const resultQuery = useQuery({
    queryKey: [...keys.conversation(decodedId), 'result'],
    queryFn: () => getRunResult(decodedId),
    enabled: !!decodedId && isTerminal,
    retry: retryResult,
    retryDelay: retryResultDelay
  })

  // Poll only while the backend reports the run still in flight. (On a fresh
  // load of a /runs/:id we don't own a live stream — the persisted record is
  // the source of truth until it settles.) An awaiting run is driven by the
  // seeded session below, so we stop polling once we've seeded it.
  const reattach = useReattachSession()
  const seededRef = useRef(false)
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    // Seed exactly once when we first observe an awaiting record with a
    // pendingSuspension — hydrates the question card + reply form on a cold page.
    if (!seededRef.current && isAwaiting && detail?.pendingSuspension && detail.executionId) {
      seededRef.current = true
      reattach.seedAwaiting(
        detail.executionId,
        toAwaitingEnvelope(detail.pendingSuspension),
        detail.pendingSuspension.roundCount
      )
    }
  }, [isAwaiting, detail, reattach])

  useEffect(() => {
    // Keep polling while running; stop once awaiting (session takes over) or terminal.
    setPollMs(isRunning ? POLL_MS : false)
  }, [isRunning])

  // The seeded session drives the awaiting UI; if its own resume produced a
  // terminal/awaiting state we trust the session over a stale poll.
  const sessionActive = reattach.state.status !== 'idle'

  const events = useMemo<TraceEvent[]>(() => {
    const raw = detail?.events ?? []
    const adapted = raw.map((e) => adaptHeartbeat(e as unknown as PersistedTraceEvent))
    const grouped = groupFanouts(adapted)
    // The persisted trace carries no terminal envelope (the record's status
    // does) — synthesize one so the journey ends with "Delivered"/"failed"
    // and the theater plays the run's ending on a cold load.
    const terminal =
      detail &&
      detail.resumable === undefined &&
      (detail.status === 'success' || detail.status === 'partial_success' || detail.status === 'failure')
    if (detail && terminal && !adapted.some((e) => e.kind === 'final' || e.kind === 'error')) {
      const atMs = detail.finishedAt ? Date.parse(detail.finishedAt) : (adapted[adapted.length - 1]?.atMs ?? Date.now())
      const base = { ts: new Date(atMs).toISOString(), atMs, seq: Number.MAX_SAFE_INTEGER }
      grouped.push(
        detail.status === 'failure'
          ? {
              ...base,
              kind: 'error',
              meta: {
                ...(detail.issues[0]?.code ? { code: detail.issues[0].code } : {}),
                ...(detail.issues[0]?.message ? { message: detail.issues[0].message } : {})
              }
            }
          : { ...base, kind: 'final', meta: { status: detail.status } }
      )
    }
    return grouped
  }, [detail])

  const baseMs = useMemo(() => {
    if (detail?.startedAt) return new Date(detail.startedAt).getTime()
    return events[0]?.atMs ?? Date.now()
  }, [detail?.startedAt, events])

  // 1 Hz tick for the running theater's elapsed clock.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (!isRunning) return
    const t = setInterval(() => setNowMs(Date.now()), 1_000)
    return () => clearInterval(t)
  }, [isRunning])

  const onReply = (reply: PlaygroundResumeReply) => {
    void reattach.respond({ reply, meta: { trigger: 'reply' } })
  }

  const [cancelError, setCancelError] = useState<string | null>(null)
  const onCancel = async () => {
    if (!decodedId) return
    setCancelling(true)
    setCancelError(null)
    try {
      await cancelRun(decodedId, { reason: 'user_cancelled' })
      reattach.cancel() // release any seeded session; server state is authoritative now
      await runQuery.refetch()
    } catch (err) {
      // Be honest: a failed server cancel means the run is still going.
      setCancelError(err instanceof PortalApiError ? describeApiError(t, err) : t.run.cancelFailed)
    } finally {
      setCancelling(false)
    }
  }

  if (!decodedId) {
    return (
      <div className="page portal-page">
        <Notice kind="danger">{t.run.missingRunId}</Notice>
      </div>
    )
  }

  if (runQuery.isLoading) {
    return (
      <div className="page portal-page">
        <div className="portal-loading">
          <Spinner size={18} /> {t.run.loadingRun}
        </div>
      </div>
    )
  }

  if (runQuery.isError || !detail) {
    return (
      <div className="page portal-page">
        <Notice kind="danger">{runQuery.error ? describeApiError(t, runQuery.error) : t.run.couldNotLoad}</Notice>
      </div>
    )
  }

  const backTo = readOnly ? '/conversations' : '/active'
  const backLabel = readOnly ? t.run.backToHistory : t.run.backToActiveRuns
  const terminalResponse = buildTerminalResponse(detail, resultQuery.data)
  const resultMeta = buildResultMeta(detail, events)

  return (
    <div className="page portal-page run-page">
      <header className="page-header">
        <div>
          <button type="button" className="btn btn-ghost btn-sm run-back" onClick={() => navigate(backTo)}>
            <Icon name="chevron" size={14} /> {backLabel}
          </button>
          <h1 className="page-title">
            {flows.data?.find((f) => f.flowId === detail.flowId)?.displayName ?? detail.displayName}
          </h1>
          <div className="page-sub">
            <span className="chip mono">{detail.executionId.slice(0, 20)}…</span>
          </div>
        </div>
      </header>

      {cancelError && <Notice kind="warning">{cancelError}</Notice>}

      {/* What was asked — collapsed to one line; expands to the full brief,
          attachments and the question→reply conversation. */}
      {detail.input && <RunRequest input={detail.input} questions={questionsAsked(events)} />}

      {detail.resumable && (
        <Notice kind="warning">
          {t.run.answerAgain(formatDateTime(detail.resumable.until))}
          {readOnly && (
            <>
              {' '}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => navigate(`/runs/${encodeURIComponent(detail.executionId)}`)}
              >
                {t.run.openToAnswer}
              </button>
            </>
          )}
        </Notice>
      )}

      {/* Awaiting on a cold page → drive the seeded session (reply works here). */}
      {isAwaiting && !readOnly && sessionActive ? (
        <LiveRunView
          state={reattach.state}
          maxRounds={detail.maxRounds}
          onReply={onReply}
          onCancel={() => void onCancel()}
          cancelling={cancelling}
          priorEvents={events}
        />
      ) : (
        <>
          {isRunning && !readOnly && (
            <div className="live-run-bar">
              <div className="live-run-status">
                <Spinner size={15} />
                <span className="live-run-headline">{currentActivity(events, t.beats) ?? t.run.workingFallback}</span>
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={cancelling}
                onClick={() => void onCancel()}
              >
                {cancelling ? <Spinner size={13} /> : <Icon name="cancel" size={13} />} {t.run.cancelRun}
              </button>
            </div>
          )}

          {isAwaiting && readOnly && !answerable && <Notice kind="warning">{t.run.pausedReadOnly}</Notice>}

          {isAwaiting && !readOnly && !sessionActive && (
            <Notice kind="warning">{t.run.awaitingDetailsUnavailable}</Notice>
          )}

          {/* Hold the deliverable card until the persisted result settles so it
              never flashes an empty "no files" state while files are loading. */}
          {isTerminal && resultQuery.isLoading && (
            <div className="portal-loading">
              <Spinner size={16} /> {t.run.retrievingDelivery}
            </div>
          )}
          {/* A run that can still be answered has not ended: its failure card would tell the person to start over. */}
          {terminalResponse && !answerable && !resultQuery.isLoading && (
            <RunResult response={terminalResponse} meta={resultMeta} executionId={detail.executionId} />
          )}

          <RunTheater key={detail.executionId} events={events} baseMs={baseMs} live={isRunning} nowMs={nowMs} />

          <RunJourney events={events} live={isRunning} truncated={detail.eventsTruncated ?? 0} />
        </>
      )}
    </div>
  )
}

/** Build the `awaiting` envelope shape `seedAwaiting` expects from the BFF's PendingSuspension. */
function toAwaitingEnvelope(p: PendingSuspension): StreamingAwaiting['awaiting'] {
  return {
    suspensionId: p.suspensionId,
    toolUseId: p.toolUseId,
    toolName: p.toolName,
    toolInput: p.toolInput,
    notifyPayload: {},
    expiresAt: p.expiresAt,
    roundCount: p.roundCount
  }
}

/**
 * Build the result card's response from the BFF's PERSISTED result when one
 * exists (reply + downloadable files via the BFF file route), else degrade to
 * the execution record's status + issues (legacy runs, expired results).
 */
function buildTerminalResponse(detail: RunDetail, result: RunResultView | undefined) {
  const terminal = detail.status === 'success' || detail.status === 'partial_success' || detail.status === 'failure'
  if (!terminal) return undefined
  if (result) {
    return {
      status: result.status,
      reply: result.reply,
      files: result.files.map((f) => ({
        name: f.name,
        mimetype: f.mimetype,
        size: f.size,
        url: runFileUrl(result.executionId, f.index)
      })),
      issues: result.issues.length > 0 ? result.issues : detail.issues
    }
  }
  return {
    status: detail.status,
    reply: undefined,
    files: undefined,
    issues: detail.issues
  }
}

/** At-a-glance facts for the result card — record fields first, events as fallback. */
function buildResultMeta(detail: RunDetail, events: TraceEvent[]): ResultMeta {
  const stats = runStats(events)
  return {
    completedAt: detail.finishedAt,
    durationMs: detail.durationMs ?? stats.durationMs,
    questions: stats.questions,
    specialists: stats.specialists,
    sources: stats.sources
  }
}
